import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowDown, ArrowUp, Check, Plus } from 'lucide-react';
import { format } from 'date-fns';
import { useAuthState } from '../hooks/useAuth';
import { useProject, useRuns } from '../hooks/useFirestoreData';
import { Button } from '../components/ui/Button';
import { DateInput } from '../components/ui/DateInput';
import { Dialog } from '../components/ui/Dialog';
import { Input, Label } from '../components/ui/Input';
import { Spinner } from '../components/ui/Spinner';
import { cn } from '../lib/utils';
import { getDb } from '../lib/firebase';
import { createRun, swapRunOrders, updateRun } from '../lib/firestore';
import type { RunDoc, RunStatus } from '@mct/shared';

export function RunsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const { user } = useAuthState();
  const { data: project } = useProject(projectId ?? null);
  const { data: runs, loading } = useRuns(projectId ?? null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const today = format(new Date(), 'yyyy-MM-dd');
  const [start, setStart] = useState(today);
  const [end, setEnd] = useState(today);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!project || !user) return;
    setBusy(true);
    setErr(null);
    try {
      const maxOrder = runs.reduce((m, r) => Math.max(m, r.order), 0);
      await createRun(getDb(), project, user.uid, {
        name,
        order: maxOrder + 1,
        plannedStartDate: start,
        plannedEndDate: end,
      });
      setCreating(false);
      setName('');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (!projectId) return null;

  return (
    <div
      data-testid="runs-page-root"
      className="h-full overflow-auto px-4 py-6 sm:px-6"
    >
      <div className="mx-auto max-w-5xl">
        <div className="flex items-center justify-between gap-2">
          <h2 className="truncate text-lg font-semibold">フェーズ管理</h2>
          <Button onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4 sm:mr-1" />
            <span className="hidden sm:inline">新規フェーズ</span>
          </Button>
        </div>

        {loading ? (
          <div className="mt-8 flex justify-center">
            <Spinner />
          </div>
        ) : runs.length === 0 ? (
          <div className="card mt-6 p-6 text-center text-sm text-slate-600">
            フェーズがまだありません
          </div>
        ) : (
          <div className="mt-6">
            <div className="hidden grid-cols-[72px_minmax(0,1fr)_auto_auto] items-center gap-4 px-4 pb-2 text-[11px] font-medium uppercase tracking-wide text-slate-400 sm:grid">
              <span>順序</span>
              <span>名前</span>
              <span>予定</span>
              <span>ステータス</span>
            </div>
            <ul className="space-y-2">
              {runs.map((r, i) => (
                <RunCard
                  key={r.runId}
                  projectId={projectId}
                  run={r}
                  prev={i > 0 ? runs[i - 1] ?? null : null}
                  next={i < runs.length - 1 ? runs[i + 1] ?? null : null}
                />
              ))}
            </ul>
          </div>
        )}
      </div>

      <Dialog
        open={creating}
        onClose={() => setCreating(false)}
        title="新規フェーズ"
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreating(false)}>
              キャンセル
            </Button>
            <Button onClick={handleCreate} disabled={busy || !name.trim()}>
              作成
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <Label htmlFor="rname">フェーズ名</Label>
            <Input
              id="rname"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="例: 移行テスト1"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="rstart">開始予定</Label>
              <DateInput
                value={start}
                onChange={(next) => setStart(next)}
                max={end}
              />
            </div>
            <div>
              <Label htmlFor="rend">終了予定</Label>
              <DateInput
                value={end}
                onChange={(next) => setEnd(next)}
                min={start}
              />
            </div>
          </div>
          <p className="text-xs text-slate-500">
            作成後、Cloud Functions が全タスクの Execution を自動生成します。
          </p>
          {err ? <p className="text-xs text-red-600">{err}</p> : null}
        </div>
      </Dialog>
    </div>
  );
}

function RunCard({
  projectId,
  run,
  prev,
  next,
}: {
  projectId: string;
  run: RunDoc;
  /** Run immediately above this one in sort order, or null if first. */
  prev: RunDoc | null;
  /** Run immediately below this one in sort order, or null if last. */
  next: RunDoc | null;
}) {
  return (
    <li className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:grid sm:grid-cols-[72px_minmax(0,1fr)_auto_auto] sm:items-center sm:gap-4 sm:p-3">
      <div className="flex items-center gap-2 sm:contents">
        <ReorderButtons projectId={projectId} run={run} prev={prev} next={next} />
        <span className="min-w-0 flex-1 truncate font-medium text-slate-800">
          {run.name}
        </span>
      </div>
      <div className="mt-3 sm:mt-0">
        <span className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-slate-400 sm:hidden">
          予定
        </span>
        <PlannedDatesEditor projectId={projectId} run={run} />
      </div>
      <div className="mt-3 sm:mt-0">
        <span className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-slate-400 sm:hidden">
          ステータス
        </span>
        <StatusPicker projectId={projectId} run={run} />
      </div>
    </li>
  );
}

function ReorderButtons({
  projectId,
  run,
  prev,
  next,
}: {
  projectId: string;
  run: RunDoc;
  prev: RunDoc | null;
  next: RunDoc | null;
}) {
  const [busy, setBusy] = useState(false);
  const swap = async (neighbor: RunDoc) => {
    if (busy) return;
    setBusy(true);
    try {
      await swapRunOrders(
        getDb(),
        projectId,
        { runId: run.runId, order: run.order },
        { runId: neighbor.runId, order: neighbor.order },
      );
    } catch (err) {
      console.error('swapRunOrders failed', err);
    } finally {
      setBusy(false);
    }
  };
  const baseCls =
    'inline-flex h-6 w-6 items-center justify-center rounded text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-500';
  return (
    <div
      className="flex shrink-0 items-center gap-0.5"
      data-testid="run-reorder-buttons"
    >
      <button
        type="button"
        aria-label="上に移動"
        title="上に移動"
        className={baseCls}
        disabled={!prev || busy}
        onClick={prev ? () => void swap(prev) : undefined}
      >
        <ArrowUp className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        aria-label="下に移動"
        title="下に移動"
        className={baseCls}
        disabled={!next || busy}
        onClick={next ? () => void swap(next) : undefined}
      >
        <ArrowDown className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

const RUN_STATUS_LABEL_JA: Record<RunStatus, string> = {
  planned: '計画中',
  active: '進行中',
  completed: '完了',
  cancelled: '中止',
};

function StatusPicker({ projectId, run }: { projectId: string; run: RunDoc }) {
  return (
    <select
      className="input !w-28 !py-1"
      value={run.status}
      onChange={(e) => {
        void updateRun(getDb(), projectId, run.runId, {
          status: e.target.value as RunStatus,
        });
      }}
    >
      {(Object.keys(RUN_STATUS_LABEL_JA) as RunStatus[]).map((s) => (
        <option key={s} value={s}>
          {RUN_STATUS_LABEL_JA[s]}
        </option>
      ))}
    </select>
  );
}

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

function PlannedDatesEditor({
  projectId,
  run,
}: {
  projectId: string;
  run: RunDoc;
}) {
  const [status, setStatus] = useState<SaveStatus>('idle');
  const savedTimerRef = useRef<number | null>(null);
  useEffect(() => {
    return () => {
      if (savedTimerRef.current) window.clearTimeout(savedTimerRef.current);
    };
  }, []);
  const commit = (
    field: 'plannedStartDate' | 'plannedEndDate',
    next: string,
  ) => {
    if (next === run[field]) return;
    if (savedTimerRef.current) {
      window.clearTimeout(savedTimerRef.current);
      savedTimerRef.current = null;
    }
    setStatus('saving');
    updateRun(getDb(), projectId, run.runId, { [field]: next })
      .then(() => {
        setStatus('saved');
        savedTimerRef.current = window.setTimeout(() => {
          setStatus('idle');
          savedTimerRef.current = null;
        }, 1500);
      })
      .catch((err) => {
        console.error('updateRun failed', err);
        setStatus('error');
      });
  };
  return (
    <div
      className="flex flex-col items-stretch gap-1.5 sm:inline-flex sm:flex-row sm:items-center sm:gap-2 sm:whitespace-nowrap"
      data-testid="run-planned-dates"
    >
      <DateInput
        value={run.plannedStartDate}
        onChange={(next) => commit('plannedStartDate', next)}
        ariaLabel="開始予定"
        className="!py-1 sm:!w-[148px]"
        max={run.plannedEndDate}
      />
      <span aria-hidden className="hidden text-slate-400 sm:inline">—</span>
      <DateInput
        value={run.plannedEndDate}
        onChange={(next) => commit('plannedEndDate', next)}
        ariaLabel="終了予定"
        className="!py-1 sm:!w-[148px]"
        min={run.plannedStartDate}
      />
      <SaveStatusBadge status={status} />
    </div>
  );
}

function SaveStatusBadge({ status }: { status: SaveStatus }) {
  return (
    <span
      aria-live="polite"
      data-testid="run-planned-dates-save-status"
      data-status={status}
      className={cn(
        'inline-flex h-4 items-center gap-1 text-xs transition-opacity duration-300 sm:ml-1 sm:w-20',
        status === 'idle' ? 'opacity-0' : 'opacity-100',
      )}
    >
      {status === 'saving' ? (
        <span className="text-slate-400">保存中…</span>
      ) : status === 'saved' ? (
        <span className="inline-flex items-center gap-0.5 text-emerald-600">
          <Check className="h-3.5 w-3.5" />
          保存しました
        </span>
      ) : status === 'error' ? (
        <span className="text-red-600">保存失敗</span>
      ) : null}
    </span>
  );
}
